// Copyright © 2021 Move Closer
import { defineComponent } from '@vue/composition-api';
import { usePagination } from '../../_abstract';
import { BootstrapButton } from '../Button';
import { BootstrapLink } from '../Link';
import { BootstrapIcon } from '../Icon';
import { bootstrapPaginationProps } from './Pagination.hooks';
/**
 * @emits change - When the User calls for the page change.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const BootstrapPagination = defineComponent({
    name: 'BootstrapPagination',
    props: bootstrapPaginationProps,
    components: {
        BootstrapButton,
        BootstrapLink,
        BootstrapIcon
    },
    emits: ['change'],
    setup(props, ctx) {
        function resolveTarget(page) {
            if (!props.link) {
                return { target: '#' };
            }
            let target;
            if (typeof props.link.target === 'string') {
                target = `${props.link.target}${props.link.target.indexOf('?') > 0 ? '&' : '?'}${props.link.param}=${page}`;
            }
            else {
                target = {
                    ...props.link.target,
                    query: {
                        ...props.link.target.query,
                        [props.link.param]: String(page)
                    }
                };
            }
            return { target };
        }
        return {
            ...usePagination(props, ctx),
            resolveTarget
        };
    },
    template: `
    <nav :aria-label="$t('_.pagination')">
    <ul class="pagination list-unstyled" :class="{ '--items-with-background': itemBackground }" v-if="totalVisible">
      <li v-if="showDirectionButtons" class="page-item" :class="{ 'disabled': !canGoBack }">
        <BootstrapLink v-if="link" class="btn btn-link page-link"
                       :link="resolveTarget(currentPage - 1)" :disabled="!canGoBack"
                       :aria-label="$t('_.previous-page')" :aria-disabled="canGoBack ? 'false' : 'true'"
                       @click.native.prevent="onPrevPageClick">
                        <BootstrapIcon name="chevron-left" size="small" />
        </BootstrapLink>
        <BootstrapButton v-else class="btn-link page-link"
                         :aria-label="$t('_.previous-page')" :aria-disabled="canGoBack ? 'false' : 'true'"
                         @click="onPrevPageClick">
                          <BootstrapIcon name="chevron-left" size="small" />
        </BootstrapButton>
      </li>

      <li v-for="(page, index) in items" :key="page + '-' + index"
          class="page-item" :class="{ 'active': page === currentPage }">
        <BootstrapLink v-if="link" class="btn btn-link page-link"
                       :link="resolveTarget(page)" :disabled="typeof page === 'string'"
                       :aria-disabled="typeof page === 'string'"
                       @click.native.prevent="onDirectPageClick(page)">{{ page }}</BootstrapLink>
        <BootstrapButton v-else class="btn-link page-link"
                         :class="{ 'disabled': typeof page === 'string' }"
                         @click="onDirectPageClick(page)">{{ page }}</BootstrapButton>
      </li>

      <li v-if="showDirectionButtons" class="page-item" :class="{ 'disabled': !canGoNext }">
        <BootstrapLink v-if="link" class="btn btn-link page-link"
                       :link="resolveTarget(currentPage + 1)" :disabled="!canGoNext"
                       :aria-label="$t('_.next-page')" :aria-disabled="canGoNext ? 'false' : 'true'"
                       @click.native.prevent="onNextPageClick">
                        <BootstrapIcon name="chevron-right" size="small" />
        </BootstrapLink>
        <BootstrapButton v-else class="btn-link page-link"
                         :aria-label="$t('_.next-page')" :aria-disabled="canGoNext ? 'false' : 'true'"
                         @click="onNextPageClick">
                          <BootstrapIcon name="chevron-right" size="small" />
        </BootstrapButton>
      </li>
    </ul>
    </nav>`
});
